import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  claimFeeNoticeAPI,
  confirmFeeNoticeAPI,
  confirmFeeNoticeMatterAPI,
  dismissFeeNoticeAPI,
  removeFeeNoticeAPI,
  fetchFeeNoticeContextAPI,
  fetchFeeNoticeInvoiceAPI,
  fetchFeeNoticeListAPI,
  fetchFeeNoticeTeamActivityAPI,
  releaseFeeNoticeAPI,
  saveFeeNoticeInvoiceAPI,
  sendFeeNoticeInvoiceAPI,
} from "../service/feeNotices";
import { searchMattersAPI } from "../service/mailIntake";

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

// One table page. Server-side: the backend slices by offset/limit and
// reports the true scope total, so the pager can walk all ~5,000+
// letters instead of dead-ending at the old fixed 500-row response.
export const QUEUE_PAGE_SIZE = 50;

// Lightweight probe used by the nav shell (PrimarySideBar / MobileTabBar) to
// decide whether to show the Fee Notices entry at all. Per the frontend
// handoff: "hide nav entry when the list endpoint 404s/errors" — this never
// rejects so a disabled/undeployed feature just quietly hides the icon
// instead of spamming the console or a global error toast.
export const fetchFeeNoticeAvailability = createAsyncThunk(
  "feeNotices/fetchAvailability",
  async () => {
    try {
      // limit=1: the probe only needs `total`, so don't make the
      // backend serialize a full page of rows for a nav badge.
      const response = await fetchFeeNoticeListAPI("active", { limit: 1 });
      // `total` is the real scope size; `count` is the returned page
      // and only a fallback for a not-yet-upgraded backend.
      return {
        available: true,
        count: response?.data?.total ?? response?.data?.count ?? 0,
      };
    } catch (error) {
      return { available: false, count: 0 };
    }
  }
);

export const fetchFeeNoticeQueue = createAsyncThunk(
  "feeNotices/fetchQueue",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { queueScope, queuePage } = getState().feeNotices;
      const response = await fetchFeeNoticeListAPI(queueScope, {
        offset: (queuePage - 1) * QUEUE_PAGE_SIZE,
        limit: QUEUE_PAGE_SIZE,
      });
      return response?.data || { items: [], count: 0, can_remove_unmatched: false };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch the fee notice queue"
      );
    }
  }
);

// Super-admin dashboard: per-agent activity over a window, plus the
// letters each agent is holding right now.
export const fetchFeeNoticeTeamActivity = createAsyncThunk(
  "feeNotices/fetchTeamActivity",
  async (_, { getState, rejectWithValue }) => {
    try {
      const days = getState().feeNotices.teamActivityDays;
      const response = await fetchFeeNoticeTeamActivityAPI(days);
      return response?.data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to load team activity"
      );
    }
  }
);

// Sorter-style soft claim, mirroring MailIntakeSlice's `claimSpecificItem`:
// a rejection here is a real error (claimed by a teammate, already
// processed) rather than an empty-queue signal, so the caller toasts
// `result.payload` directly.
export const claimFeeNotice = createAsyncThunk(
  "feeNotices/claim",
  async (id, { rejectWithValue }) => {
    try {
      const response = await claimFeeNoticeAPI(id);
      return response?.data?.item ?? null;
    } catch (error) {
      return rejectWithValue(error.message || "Could not open this letter");
    }
  }
);

export const releaseFeeNotice = createAsyncThunk(
  "feeNotices/release",
  async (id, { rejectWithValue }) => {
    try {
      const response = await releaseFeeNoticeAPI(id);
      return response?.data?.item ?? null;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to release this letter"
      );
    }
  }
);

export const confirmFeeNoticeMatter = createAsyncThunk(
  "feeNotices/confirmMatter",
  async ({ id, matterId }, { rejectWithValue }) => {
    try {
      const response = await confirmFeeNoticeMatterAPI(id, matterId);
      return response?.data?.item ?? null;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to confirm the matter"
      );
    }
  }
);

export const fetchFeeNoticeContext = createAsyncThunk(
  "feeNotices/fetchContext",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchFeeNoticeContextAPI(id);
      return response?.data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to load the review sidebar"
      );
    }
  }
);

// The "go" button. `fields` is the full canonical fee-section payload
// (see feeMath.js) — stored durably by the backend regardless of the
// Salesforce write flag, so a confirm here is never lost even before
// FEE_NOTICE_SF_WRITE_ENABLED is armed.
export const confirmFeeNotice = createAsyncThunk(
  "feeNotices/confirm",
  async ({ id, fields, noFeeAwarded }, { rejectWithValue }) => {
    try {
      const response = await confirmFeeNoticeAPI(id, fields, noFeeAwarded);
      return response?.data?.item ?? null;
    } catch (error) {
      // Object rather than a bare string, unlike its neighbours: a
      // confirm can be refused because the letter still owes the client
      // a cost invoice, and the caller has to be able to tell that
      // apart to route the reviewer somewhere useful.
      return rejectWithValue({
        message: error.message || "Failed to confirm this letter",
        code: error.code,
      });
    }
  }
);

export const dismissFeeNotice = createAsyncThunk(
  "feeNotices/dismiss",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await dismissFeeNoticeAPI(id, reason);
      return response?.data?.item ?? null;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to dismiss this letter"
      );
    }
  }
);

export const removeFeeNotice = createAsyncThunk(
  "feeNotices/remove",
  async (id, { rejectWithValue }) => {
    try {
      const response = await removeFeeNoticeAPI(id);
      return response?.data?.item ?? null;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to remove this letter from the queue"
      );
    }
  }
);

// --------------------------------------------------------------------------
// Cost-reimbursement invoice (Matthew, 2026-07-27: view/edit/send from the
// inbox). The whole GET/POST response payload is kept as-is in state —
// invoice, composed email text, send availability, and send history — so
// the InvoiceSection renders exactly what the backend will send.
// --------------------------------------------------------------------------

export const fetchFeeNoticeInvoice = createAsyncThunk(
  "feeNotices/fetchInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchFeeNoticeInvoiceAPI(id);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load the invoice");
    }
  }
);

export const saveFeeNoticeInvoice = createAsyncThunk(
  "feeNotices/saveInvoice",
  async ({ id, invoice }, { rejectWithValue }) => {
    try {
      const response = await saveFeeNoticeInvoiceAPI(id, invoice);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to save the invoice");
    }
  }
);

export const sendFeeNoticeInvoice = createAsyncThunk(
  "feeNotices/sendInvoice",
  async ({ id, to }, { rejectWithValue }) => {
    try {
      const response = await sendFeeNoticeInvoiceAPI(id, to);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to send the invoice");
    }
  }
);

// Reuses the mail sorter's matter-search endpoint (same PublicMatter ids
// finance confirms against via POST .../matter/) — kept in this slice
// rather than dispatching MailIntakeSlice's own thunk so the two features'
// search state never cross-pollute when both are open in the same session.
export const searchFeeNoticeMatters = createAsyncThunk(
  "feeNotices/searchMatters",
  async (query, { rejectWithValue }) => {
    try {
      const response = await searchMattersAPI(query);
      return response?.data?.results || [];
    } catch (error) {
      return rejectWithValue(error.message || "Search failed");
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const initialState = {
  // null = not yet checked. The nav shell renders nothing for the fee
  // notice icon until this resolves, so it never flashes then disappears.
  available: null,
  activeCount: 0,

  view: "list", // "list" | "review" | "activity" (admin dashboard)

  queue: { items: [], count: 0 },
  queueLoading: false,
  queueScope: "active", // "active" | "all" | "confirmed" | "dismissed"
  queuePage: 1, // 1-based; offset = (queuePage - 1) * QUEUE_PAGE_SIZE

  // Super-admin team dashboard.
  teamActivity: null,
  teamActivityLoading: false,
  teamActivityError: null,
  teamActivityDays: 7,

  currentItem: null,
  currentItemLoading: false,
  // Bumped on every successful claim so ReviewView can reset local UI
  // even when the same letter is re-opened after Back to queue (the
  // pane stays mounted for document-engine warmth, so currentItem.id
  // alone does not remount it).
  reviewSession: 0,
  matterUpdating: false,

  matterSearchResults: [],
  matterSearchLoading: false,

  context: null,
  contextLoading: false,
  contextError: null,

  confirming: false,
  dismissing: false,
  removing: false,
  canRemoveUnmatched: false,

  // Cost-reimbursement invoice panel (the raw invoice endpoint payload).
  invoice: null,
  invoiceLoading: false,
  invoiceSaving: false,
  invoiceSending: false,
  invoiceError: null,
};

const FeeNoticeSlice = createSlice({
  name: "feeNotices",
  initialState,
  reducers: {
    setQueueScope: (state, action) => {
      state.queueScope = action.payload;
      // A new scope is a new list; page 3 of "Active" means nothing
      // in "Confirmed".
      state.queuePage = 1;
    },
    setQueuePage: (state, action) => {
      state.queuePage = action.payload;
    },
    setTeamActivityDays: (state, action) => {
      state.teamActivityDays = action.payload;
    },
    setFeeNoticeView: (state, action) => {
      state.view = action.payload;
    },
    clearCurrentFeeNotice: (state) => {
      state.currentItem = null;
      state.context = null;
      state.contextError = null;
      state.invoice = null;
      state.invoiceError = null;
    },
    clearFeeNoticeMatterSearch: (state) => {
      state.matterSearchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Availability probe
      .addCase(fetchFeeNoticeAvailability.fulfilled, (state, action) => {
        state.available = action.payload.available;
        state.activeCount = action.payload.count;
      })

      // Queue list
      .addCase(fetchFeeNoticeQueue.pending, (state) => {
        state.queueLoading = true;
      })
      .addCase(fetchFeeNoticeQueue.fulfilled, (state, action) => {
        state.queue = action.payload;
        state.queueLoading = false;
        state.available = true;
        state.canRemoveUnmatched = Boolean(
          action.payload?.can_remove_unmatched,
        );
      })
      .addCase(fetchFeeNoticeQueue.rejected, (state) => {
        state.queueLoading = false;
        state.queue = { items: [], count: 0 };
        state.canRemoveUnmatched = false;
      })

      // Team activity dashboard
      .addCase(fetchFeeNoticeTeamActivity.pending, (state) => {
        state.teamActivityLoading = true;
        state.teamActivityError = null;
      })
      .addCase(fetchFeeNoticeTeamActivity.fulfilled, (state, action) => {
        state.teamActivity = action.payload;
        state.teamActivityLoading = false;
      })
      .addCase(fetchFeeNoticeTeamActivity.rejected, (state, action) => {
        state.teamActivityLoading = false;
        state.teamActivityError =
          action.payload || "Failed to load team activity";
      })

      // Claim
      .addCase(claimFeeNotice.pending, (state) => {
        state.currentItemLoading = true;
      })
      .addCase(claimFeeNotice.fulfilled, (state, action) => {
        state.currentItem = action.payload;
        state.currentItemLoading = false;
        state.view = "review";
        state.reviewSession += 1;
        state.context = null;
        state.contextError = null;
        state.invoice = null;
        state.invoiceError = null;
      })
      .addCase(claimFeeNotice.rejected, (state) => {
        state.currentItemLoading = false;
      })

      // Release
      .addCase(releaseFeeNotice.fulfilled, (state) => {
        // Kept for engine warmth — see confirmFeeNotice.fulfilled.
        state.context = null;
        state.contextError = null;
        state.view = "list";
      })

      // Confirm matter
      .addCase(confirmFeeNoticeMatter.pending, (state) => {
        state.matterUpdating = true;
      })
      .addCase(confirmFeeNoticeMatter.fulfilled, (state, action) => {
        state.currentItem = action.payload;
        state.matterUpdating = false;
        // A re-pointed matter invalidates any sidebar data fetched for
        // the previous one — the invoice included (its costs and client
        // came from the old matter).
        state.context = null;
        state.contextError = null;
        state.invoice = null;
        state.invoiceError = null;
      })
      .addCase(confirmFeeNoticeMatter.rejected, (state) => {
        state.matterUpdating = false;
      })

      // Sidebar context
      .addCase(fetchFeeNoticeContext.pending, (state) => {
        state.contextLoading = true;
        state.contextError = null;
      })
      .addCase(fetchFeeNoticeContext.fulfilled, (state, action) => {
        state.context = action.payload;
        state.contextLoading = false;
      })
      .addCase(fetchFeeNoticeContext.rejected, (state, action) => {
        state.contextLoading = false;
        state.contextError = action.payload || "Failed to load the review sidebar";
      })

      // Confirm (the "go" button)
      .addCase(confirmFeeNotice.pending, (state) => {
        state.confirming = true;
      })
      .addCase(confirmFeeNotice.fulfilled, (state) => {
        state.confirming = false;
        // currentItem is deliberately KEPT (here and on dismiss/
        // release): it means "the letter the hidden review pane last
        // showed". Clearing it unmounts the pane and with it the
        // Apryse engine, putting a full WASM boot in front of the next
        // letter. The pane is invisible once view flips to "list", and
        // claiming the next letter replaces the item wholesale.
        state.context = null;
        state.view = "list";
      })
      .addCase(confirmFeeNotice.rejected, (state) => {
        state.confirming = false;
      })

      // Dismiss
      .addCase(dismissFeeNotice.pending, (state) => {
        state.dismissing = true;
      })
      .addCase(dismissFeeNotice.fulfilled, (state) => {
        state.dismissing = false;
        // Kept for engine warmth — see confirmFeeNotice.fulfilled.
        state.context = null;
        state.view = "list";
      })
      .addCase(dismissFeeNotice.rejected, (state) => {
        state.dismissing = false;
      })

      .addCase(removeFeeNotice.pending, (state) => {
        state.removing = true;
      })
      .addCase(removeFeeNotice.fulfilled, (state) => {
        state.removing = false;
        state.context = null;
        state.view = "list";
      })
      .addCase(removeFeeNotice.rejected, (state) => {
        state.removing = false;
      })

      // Invoice: fetch / save / send. Save and send responses carry the
      // same payload shape as the GET, so each one simply replaces (or
      // patches) `state.invoice`.
      .addCase(fetchFeeNoticeInvoice.pending, (state) => {
        state.invoiceLoading = true;
        state.invoiceError = null;
      })
      .addCase(fetchFeeNoticeInvoice.fulfilled, (state, action) => {
        state.invoice = action.payload;
        state.invoiceLoading = false;
      })
      .addCase(fetchFeeNoticeInvoice.rejected, (state, action) => {
        state.invoiceLoading = false;
        state.invoiceError =
          action.payload || "Failed to load the invoice";
      })
      .addCase(saveFeeNoticeInvoice.pending, (state) => {
        state.invoiceSaving = true;
        state.invoiceError = null;
      })
      .addCase(saveFeeNoticeInvoice.fulfilled, (state, action) => {
        state.invoice = action.payload;
        state.invoiceSaving = false;
      })
      .addCase(saveFeeNoticeInvoice.rejected, (state, action) => {
        state.invoiceSaving = false;
        state.invoiceError =
          action.payload || "Failed to save the invoice";
      })
      .addCase(sendFeeNoticeInvoice.pending, (state) => {
        state.invoiceSending = true;
        state.invoiceError = null;
      })
      .addCase(sendFeeNoticeInvoice.fulfilled, (state, action) => {
        state.invoiceSending = false;
        if (state.invoice) {
          state.invoice.sends = action.payload?.sends || [];
          state.invoice.last_send = action.payload?.sent || null;
          // The send is what mints (or replaces) the Payload payment
          // link, so the response carries the updated invoice — patch
          // it in so the Payment block shows the link that actually
          // went out without waiting for a refetch. Older backends
          // omit these keys; leave the state untouched then.
          if (action.payload?.invoice) {
            state.invoice.invoice = action.payload.invoice;
          }
          if (action.payload?.payment_status !== undefined) {
            state.invoice.payment_status = action.payload.payment_status;
          }
          // Sending is the thing that lifts the Confirm block, so the
          // block has to be re-read here. Without this the screen keeps
          // the assessment it got on arrival — invoice owed, Confirm
          // refused — and the reviewer is left with a dead Confirm
          // button under a strip saying the invoice went out (Matthew,
          // 2026-08-28, on the Dooley letter). A page reload was the
          // only way through.
          //
          // The fallback covers a frontend deployed ahead of the
          // backend that answers it: the send succeeded, so the letter
          // no longer owes an unsent invoice either way.
          state.invoice.confirm_gate = action.payload?.confirm_gate ?? {
            blocked: false,
            code: "already_sent",
            reason: "",
          };
        }
      })
      .addCase(sendFeeNoticeInvoice.rejected, (state, action) => {
        state.invoiceSending = false;
        state.invoiceError =
          action.payload || "Failed to send the invoice";
      })

      // Matter search
      .addCase(searchFeeNoticeMatters.pending, (state) => {
        state.matterSearchLoading = true;
      })
      .addCase(searchFeeNoticeMatters.fulfilled, (state, action) => {
        state.matterSearchResults = action.payload;
        state.matterSearchLoading = false;
      })
      .addCase(searchFeeNoticeMatters.rejected, (state) => {
        state.matterSearchLoading = false;
        state.matterSearchResults = [];
      });
  },
});

export const {
  setQueueScope,
  setQueuePage,
  setTeamActivityDays,
  setFeeNoticeView,
  clearCurrentFeeNotice,
  clearFeeNoticeMatterSearch,
} = FeeNoticeSlice.actions;

// Optional chaining guards test suites that mount PrimarySideBar / MobileTabBar
// against a hand-rolled mock store missing this slice (see
// PrimarySideBar.test.js) — those callers only care about auth/nav state and
// never registered feeNotices, so this must degrade to "not available"
// rather than throwing.
export const selectFeeNoticeAvailable = (state) => state.feeNotices?.available;
export const selectFeeNoticeActiveCount = (state) =>
  state.feeNotices?.activeCount ?? 0;

export default FeeNoticeSlice.reducer;
